import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

// Auth endpoints own their own error UI — a 401 there is a bad password, not an expired session.
const AUTH_PATHS = ['/auth/login', '/auth/me', '/auth/magic-link', '/auth/magic-verify'];
const LOGIN_PATHS = ['/login', '/portal/login', '/portal/verify'];

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('fc_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || '';
    const status = error?.response?.status;
    const isAuthCall = AUTH_PATHS.some(p => url.startsWith(p));

    if (status === 401 && !isAuthCall) {
      const path = window.location.pathname;
      if (!LOGIN_PATHS.includes(path)) {
        // A hard redirect unmounts the Toaster, so a toast here would never be
        // seen — the login screen itself communicates the expired session.
        // Matches the existing logout() behaviour in lib/auth.jsx.
        localStorage.removeItem('fc_token');
        window.location.href = path.startsWith('/portal') ? '/portal/login' : '/login';
      }
      return Promise.reject(error);
    }

    // Reads have no catch blocks and would otherwise blank the page silently.
    // Mutations already toast from their own catch blocks — don't duplicate.
    const isRead = (error?.config?.method || 'get').toLowerCase() === 'get';
    if (isRead && !isAuthCall) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : 'Could not load data. Please try again.');
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
};

export const customersApi = {
  list: () => api.get('/customers').then(r => r.data),
  get: (id) => api.get(`/customers/${id}`).then(r => r.data),
  create: (data) => api.post('/customers', data).then(r => r.data),
  update: (id, data) => api.put(`/customers/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/customers/${id}`).then(r => r.data),
};

export const propertiesApi = {
  list: (params) => api.get('/properties', { params }).then(r => r.data),
  get: (id) => api.get(`/properties/${id}`).then(r => r.data),
  create: (data) => api.post('/properties', data).then(r => r.data),
  update: (id, data) => api.put(`/properties/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/properties/${id}`).then(r => r.data),
  syncIcal: (id) => api.post(`/properties/${id}/sync-ical`).then(r => r.data),
};

export const jobsApi = {
  list: (params) => api.get('/jobs', { params }).then(r => r.data),
  get: (id) => api.get(`/jobs/${id}`).then(r => r.data),
  create: (data) => api.post('/jobs', data).then(r => r.data),
  update: (id, data) => api.put(`/jobs/${id}`, data).then(r => r.data),
  transition: (id, new_status) => api.post(`/jobs/${id}/transition`, { new_status }).then(r => r.data),
  delete: (id) => api.delete(`/jobs/${id}`).then(r => r.data),
  setKanban: (id, stage_id) => api.post(`/jobs/${id}/kanban-stage`, null, { params: { stage_id } }).then(r => r.data),
  initChecklist: (id) => api.post(`/jobs/${id}/checklist/init`).then(r => r.data),
  completeChecklistItem: (jobId, itemId, payload) => api.post(`/jobs/${jobId}/checklist/${itemId}/complete`, payload).then(r => r.data),
};

export const estimatesApi = {
  list: () => api.get('/estimates').then(r => r.data),
  create: (data) => api.post('/estimates', data).then(r => r.data),
  send: (id) => api.post(`/estimates/${id}/send`).then(r => r.data),
  approve: (id) => api.post(`/estimates/${id}/approve`).then(r => r.data),
  delete: (id) => api.delete(`/estimates/${id}`).then(r => r.data),
};

export const invoicesApi = {
  list: () => api.get('/invoices').then(r => r.data),
  get: (id) => api.get(`/invoices/${id}`).then(r => r.data),
  create: (data) => api.post('/invoices', data).then(r => r.data),
  remind: (id) => api.post(`/invoices/${id}/remind`).then(r => r.data),
  delete: (id) => api.delete(`/invoices/${id}`).then(r => r.data),
};

export const paymentsApi = {
  list: () => api.get('/payments').then(r => r.data),
  create: (data) => api.post('/payments', data).then(r => r.data),
};

export const priceBookApi = {
  list: () => api.get('/pricebook').then(r => r.data),
  create: (data) => api.post('/pricebook', data).then(r => r.data),
  update: (id, data) => api.put(`/pricebook/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/pricebook/${id}`).then(r => r.data),
};

export const pricingRulesApi = {
  list: () => api.get('/pricing-rules').then(r => r.data),
  create: (data) => api.post('/pricing-rules', data).then(r => r.data),
  update: (id, data) => api.put(`/pricing-rules/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/pricing-rules/${id}`).then(r => r.data),
  compute: (payload) => api.post('/pricing-rules/compute', payload).then(r => r.data),
};

export const checklistApi = {
  listTemplates: (params) => api.get('/checklist-templates', { params }).then(r => r.data),
  createTemplate: (data) => api.post('/checklist-templates', data).then(r => r.data),
  updateTemplate: (id, data) => api.put(`/checklist-templates/${id}`, data).then(r => r.data),
  deleteTemplate: (id) => api.delete(`/checklist-templates/${id}`).then(r => r.data),
};

export const inventoryApi = {
  list: (params) => api.get('/inventory', { params }).then(r => r.data),
  create: (data) => api.post('/inventory', data).then(r => r.data),
  update: (id, data) => api.put(`/inventory/${id}`, data).then(r => r.data),
  adjust: (id, delta, note) => api.post(`/inventory/${id}/adjust`, { delta, note }).then(r => r.data),
  delete: (id) => api.delete(`/inventory/${id}`).then(r => r.data),
};

export const issuesApi = {
  list: (params) => api.get('/issues', { params }).then(r => r.data),
  create: (data) => api.post('/issues', data).then(r => r.data),
  changeStatus: (id, new_status, resolution_note) => api.post(`/issues/${id}/status`, { new_status, resolution_note }).then(r => r.data),
  delete: (id) => api.delete(`/issues/${id}`).then(r => r.data),
};

export const notificationApi = {
  listTemplates: () => api.get('/notification-templates').then(r => r.data),
  updateTemplate: (id, data) => api.put(`/notification-templates/${id}`, data).then(r => r.data),
  outbox: () => api.get('/notifications-outbox').then(r => r.data),
};

export const kanbanApi = {
  list: () => api.get('/kanban-stages').then(r => r.data),
  create: (data) => api.post('/kanban-stages', data).then(r => r.data),
  update: (id, data) => api.put(`/kanban-stages/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/kanban-stages/${id}`).then(r => r.data),
};

export const messagesApi = {
  list: (params) => api.get('/messages', { params }).then(r => r.data),
  create: (data) => api.post('/messages', data).then(r => r.data),
  threads: () => api.get('/inbox/threads').then(r => r.data),
  markRead: (customerId) => api.post(`/inbox/threads/${customerId}/read`).then(r => r.data),
};

export const dashboardApi = {
  get: () => api.get('/dashboard').then(r => r.data),
};

export const reportsApi = {
  get: (report) => api.get(`/reports/${report}`).then(r => r.data),
};

export const usersApi = {
  list: () => api.get('/users').then(r => r.data),
  inviteCleaner: (data) => api.post('/users/invite-cleaner', data).then(r => r.data),
};

export const portalApi = {
  me: () => api.get('/portal/me').then(r => r.data),
  invoices: () => api.get('/portal/invoices').then(r => r.data),
  invoice: (id) => api.get(`/portal/invoices/${id}`).then(r => r.data),
  payInvoice: (id, payload) => api.post(`/portal/invoices/${id}/pay`, payload).then(r => r.data),
  jobs: () => api.get('/portal/jobs').then(r => r.data),
  messages: () => api.get('/portal/messages').then(r => r.data),
  sendMessage: (body, photo_ids = []) => api.post('/portal/messages', { body, photo_ids }).then(r => r.data),
  serviceRequests: () => api.get('/portal/service-requests').then(r => r.data),
  createServiceRequest: (data) => api.post('/portal/service-requests', data).then(r => r.data),
  acceptServiceRequest: (id) => api.post(`/service-requests/${id}/accept`).then(r => r.data),
  declineServiceRequest: (id) => api.post(`/service-requests/${id}/decline`).then(r => r.data),
  properties: () => api.get('/portal/properties').then(r => r.data),
  setGoogleCalendar: (pid, url) => api.put(`/portal/properties/${pid}/google-calendar`, { google_calendar_ical_url: url }).then(r => r.data),
  syncProperty: (pid) => api.post(`/portal/properties/${pid}/sync`).then(r => r.data),
  reports: () => api.get('/portal/reports').then(r => r.data),
  createReport: (data) => api.post('/portal/reports', data).then(r => r.data),
};

export const serviceRequestsApi = {
  list: (params) => api.get('/service-requests', { params }).then(r => r.data),
  get: (id) => api.get(`/service-requests/${id}`).then(r => r.data),
  quote: (id, payload) => api.post(`/service-requests/${id}/quote`, payload).then(r => r.data),
  accept: (id) => api.post(`/service-requests/${id}/accept`).then(r => r.data),
  decline: (id) => api.post(`/service-requests/${id}/decline`).then(r => r.data),
};

export const authExtraApi = {
  invitePortal: (customer_id) => api.post(`/customers/${customer_id}/invite-portal`).then(r => r.data),
  magicLinkRequest: (email) => api.post('/auth/magic-link', { email }).then(r => r.data),
  magicVerify: (token) => api.post('/auth/magic-verify', { token }).then(r => r.data),
  setPassword: (new_password) => api.post('/auth/set-password', { new_password }).then(r => r.data),
};

export const uploadApi = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
};

export function uploadUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${BACKEND_URL}${u}`;
}
